import * as React from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "@mui/material/styles/styled";
import {
  AkFeatureSelection,
  FeatureWithName,
  SearchOptions,
} from "fe-web-components/autocomplete";
import { Button, Fade, Theme } from "@mui/material";
import { PatientColorMap } from "../../layout/patientColorMap";
import { ReactComponent as SearchIcon } from "../../assets/searchIcon.svg";
import { useTranslate } from "../../hooks/useTranslate";
import { usePatient } from "../../state/patientContext";
import { KahunCategories } from "shared";
import {
  EnterUserSymptom,
  EnterUserSymptomTextOverride,
} from "../Questions/enterUserSymptom";
import { buildFreeTextQF } from "fe-common/logic/qualifiedFeature/freeText";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useWidgetSettings } from "../../widget/useWidgetSettings";
import { useNavigation } from "../../state/navigationContext";
import { onRtl } from "fe-web-components/rtl/rtlStyles";
import clsx from "clsx";

const MAX_SUGGESTIONS = 5;

//region [[ Styles ]]
const SearchComponentView = styled("div")({
  display: "flex",
  marginTop: "10px",
  marginBottom: "15px",
  flexDirection: "column",
});

const SearchView = styled("div")(
  ({ rounded }: { rounded: "true" | "false" }) => ({
    border: "1px solid #AAB0B7",
    borderRadius: "30px",
    borderBottomLeftRadius: rounded === "true" ? "0px" : "30px",
    borderBottomRightRadius: rounded === "true" ? "0px" : "30px",
    "& .search-symptom svg": {
      fontSize: "24px",
      color: PatientColorMap.Default,
    },
    "& .search-symptom .patient-MuiInputBase-input input[type=email], & .search-symptom .patient-MuiInputBase-input input[type=number], & .search-symptom .patient-MuiInputBase-input input[type=password], & .search-symptom .patient-MuiInputBase-input input[type=reset], & .search-symptom .patient-MuiInputBase-input input[type=search], & .patient-MuiInputBase-input .search-symptom input[type=tel], & .search-symptom .patient-MuiInputBase-input input[type=text], & .search-symptom .patient-MuiInputBase-input input[type=url], & .search-symptom .patient-MuiInputBase-input select, & .search-symptom .patient-MuiInputBase-input textarea": {
      // fontFamily: FONT_FAMILY_LATIN,
      // ...onRtl({
      //   fontFamily: FONT_FAMILY_HEB,
      // }),
      color: PatientColorMap.Default,
      padding: "6px 0 7px",
    },
    transition: "border-radius 0.3s ease-out",
    background: PatientColorMap.White,
  }),
);

const SearchIconStyled = styled(SearchIcon)({
  ...onRtl({
    transform: "rotateY(180deg)",
  }),
});

const SuggestionsList = styled("div")(
  ({
    items,
    showusersymptom,
  }: {
    items: number;
    showusersymptom: "true" | "false";
  }) => ({
    overflow: "hidden",
    transition: "all 0.3s, ease-out",
    minHeight: `${items * 40}px`,
    marginLeft: "0px",
    borderTopLeftRadius: "0px",
    borderTopRightRadius: "0px",
    "& .suggestion": {
      padding: "10px 20px",
    },
    "& .suggestion:first-child": {
      paddingTop: 20,
    },
    "& .suggestion:last-child": {
      paddingBottom: 20,
    },
    borderBottom:
      showusersymptom === "true" && items > 0 ? "1px solid #AAB0B7" : "none",
    background: PatientColorMap.White,
  }),
);

const SuggestionItem = styled("div")({
  cursor: "pointer",
  fontWeight: 500,
  fontSize: "16px",
  paddingBottom: "13px",
  color: PatientColorMap.Default,
});

const AddSymptomContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  marginLeft: 0,
  padding: "8px 20px 10px 20px",
  gap: 10,
  background: PatientColorMap.White,
});

const AddSymptomLabel = styled("div")({
  fontSize: "14px",
  fontWeight: 500,
});

const AddSymptomButton = styled(Button)({
  "&.add-symptom-button": {
    border: "2px solid " + PatientColorMap.OrganMapBackground,
    color: PatientColorMap.Primary,
    lineHeight: 1.2,
    borderRadius: "67px",
    pointerEvents: "all",
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    textTransform: "none",
    background: "transparent",
    padding: "2px 0",
    boxShadow: "none",
  },
});

const TellUsButton = styled(Button)({
  "&.tell-us-button": {
    border: "2px solid " + PatientColorMap.OrganMapBackground,
    color: PatientColorMap.Primary,
    textTransform: "capitalize",
    width: "90px",
    borderRadius: "67px",
    pointerEvents: "all",
    paddingLeft: 0,
    paddingRight: 0,
    background: "transparent",
    padding: "2px 0",
    boxShadow: "none",
  },
});

const InnerContainer = styled("div")(
  ({ open }: { theme?: Theme; open: 1 | 0 }) => ({
    border: "none",
    overflow: "hidden",
    height: 0,
    "&[open]": {
      border: "1px solid #AAB0B7",
      borderTop: "unset",
      boxSizing: "border-box",
      height: "auto",
    },
    borderBottomLeftRadius: "30px",
    borderBottomRightRadius: "30px",
  }),
);

//endregion [[ Styles ]]

//region [[ Props ]]

export enum UserEnterSymptomsMode {
  NONE,
  TELL_US,
  FREE_TEXT,
}

export interface SearchComponentProps {
  className?: string;
  onChange: (
    event: React.ChangeEvent<{}>,
    value: FeatureWithName | null,
  ) => void;
  disabled?: boolean;
  selected?: any;
  userEnterSymptom?: UserEnterSymptomsMode;
  onUserSymptomSent?: () => void;
  placeholder?: string;
  searchOptions: SearchOptions;
  filteredIds?: string[]; // Don't show any of these ids in the search
  noIcon?: boolean;
  autoFocus?: boolean;
  enterSymptomTextOverride?: EnterUserSymptomTextOverride;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const SearchComponent = forwardRef<HTMLDivElement, SearchComponentProps>(
  ({ onChange, ...props }) => {
    const [suggestions, setSuggestions] = useState(null);
    const [isTyping, setIsTyping] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [term, setTerm] = useState("");
    const [showUserSymptom, setShowUserSymptom] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selected, setSelected] = useState(null);

    const addSymptomRef = useRef<HTMLDivElement>();

    const userEnterSymptomEnabled =
      props.userEnterSymptom !== UserEnterSymptomsMode.NONE;

    const { gender } = usePatient();

    /* Automatically add gender to settings */
    const searchOptions = { gender, ...props.searchOptions };

    const { setViewChanged } = useNavigation();

    const [showEnterSymptom, setShowEnterSymptom] = useState(false);

    const modalRef = useRef(null);
    const ref = useRef(null);

    const { translate } = useTranslate();

    const firstResultRef = useRef<HTMLDivElement>();

    const handleSuggestions = useCallback(
      (suggestionsResp: FeatureWithName[]) => {
        if (!isLoading)
          setSuggestions(suggestionsResp.slice(0, MAX_SUGGESTIONS));
      },
      [isLoading],
    );

    const createSuggestionSelectedHandler = useCallback(
      (suggestion: any) => (e) => {
        if (props.selected === suggestion?.value?.feature?.shortname) {
          // If we selected the selected, don't report to parent,
          // and reset selected back to the value
          // based on a bug from monday:
          //https://kahun-team.monday.com/protected_static/5473757/resources/894682298/Screen%20Recording%202023-05-30%20at%209.46.05.mov
          setTerm(null);
          setSelected(props.selected);
        }
        setTerm("");
        onChange(e, suggestion);
        setIsTyping(false);
      },
      [onChange, setTerm, setIsTyping, props.selected],
    );

    const handleTermChange = useCallback(
      (newTerm: string) => {
        setTerm(newTerm);
        setSelected(null);
      },
      [setTerm],
    );

    const getPlaceholder = () => {
      if (props.placeholder) return props.placeholder;
      if (
        searchOptions.categories.find(
          (category) => category === KahunCategories.MEDICINE,
        )
      ) {
        return "Select Medicine";
      }
      if (
        searchOptions.categories.find(
          (category) => category === KahunCategories.ALLERGIES,
        )
      ) {
        return "Select Allergy";
      }
      return translate("SEARCH_PLACEHOLDER");
    };

    const filterSuggestions = useMemo(() => {
      return suggestions?.filter(
        (suggestion) =>
          !props.filteredIds?.some((id) => id === suggestion?.value?.id),
      );
    }, [suggestions, props.filteredIds]);

    const handleTextBlur = (e) => {
      if (
        addSymptomRef?.current?.contains?.(e.target) ||
        e.target.id === "suggestion"
      )
        return;
      setIsTyping(false);
    };

    useEffect(() => {
      if (isLoading) {
        setShowUserSymptom(false);
      } else {
        setShowUserSymptom(
          userEnterSymptomEnabled &&
            (term.length >= 3 || //more then 3 chars
              (term.length > 1 && filterSuggestions?.length === 0)), //no results at all,
        );
      }
    }, [userEnterSymptomEnabled, term, filterSuggestions, isLoading]);

    useEffect(
      () =>
        setShowSuggestions(
          term && (filterSuggestions?.length > 0 || showUserSymptom),
        ),
      [filterSuggestions, showUserSymptom, term],
    );

    useEffect(() => {
      // Scroll to the first results if results are shown
      if (isTyping && firstResultRef.current) {
        setTimeout(() => {
          firstResultRef.current?.scrollIntoView?.({
            behavior: "smooth",
            block: filterSuggestions?.length ? "start" : "nearest",
          });
        }, 400);
      }
    }, [suggestions, isTyping, filterSuggestions]);

    const toSearchResultOption = (suggestion: FeatureWithName, index) => (
      <Fade
        timeout={500}
        key={`suggestion-${index}`}
        in
        mountOnEnter
        unmountOnExit
      >
        <SuggestionItem
          className={"suggestion"}
          onClick={createSuggestionSelectedHandler(suggestion)}
        >
          {suggestion.value.feature.alias || suggestion.name}
        </SuggestionItem>
      </Fade>
    );

    const autoFocus = props?.autoFocus !== false; // Make null act like true

    const { onUserSymptomSent } = props;
    const onFreeTextSelection = useCallback(
      (selection: string) => {
        onUserSymptomSent && onUserSymptomSent();
        onChange(null, {
          value: buildFreeTextQF(selection) as any,
          name: selection,
        });
      },
      [onUserSymptomSent, onChange],
    );
    const { partnerId } = useWidgetSettings();

    const onEnterUserSymptom = (event) => {
      const trimmedTerm = term.trim();
      evt({
        category: CATEGORY.PATIENT_CHAT,
        action: ACTION.PATIENT_ADD_FREE_TEXT,
        label:
          translate(
            props?.enterSymptomTextOverride?.placeholderText ||
              "CANT_FIND_A_SYMPTOM",
          ) +
          " - " +
          trimmedTerm,
        partnerId,
      });
      if (props.userEnterSymptom === UserEnterSymptomsMode.FREE_TEXT)
        onFreeTextSelection(trimmedTerm);
      setIsTyping(false);
      setTerm("");
    };

    const onClickTellUsMore = (e) => {
      setShowEnterSymptom(true);
      setViewChanged(true);
      // Hide once clicked
      setIsTyping(false);
    };

    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          handleTextBlur(event);
        } else {
        }
      }
      // Bind the event listener
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("touchstart", handleClickOutside);
      };
    }, [modalRef]);

    useEffect(() => {
      setSelected(props.selected);
    }, [props.selected]);

    return (
      <SearchComponentView
        className={clsx(
          "search-component-view",
          "disable-text-selection", // Try to fix a bug when there is placeholder selection instead of entering the input
          props.className,
          props.disabled ? "disabled" : "",
        )}
        ref={ref}
      >
        <SearchView
          className={"search-view"}
          rounded={showSuggestions ? "true" : "false"}
          ref={firstResultRef}
        >
          <AkFeatureSelection
            className={"search-symptom"}
            placeholder={getPlaceholder()}
            inputProps={{ "aria-label": "Find Symptom" }}
            icon={props.noIcon ? null : <SearchIconStyled />}
            onSuggestions={handleSuggestions}
            selectedValue={selected || term}
            disabled={props.disabled}
            ref={modalRef}
            searchOptions={searchOptions}
            onOpen={() => {
              setIsTyping(true);
            }}
            onLoading={(loading) => setIsLoading(loading)}
            hideIcon={props.noIcon || false}
            term={term}
            onTermChanged={handleTermChange}
            autoFocus={autoFocus}
            hideResults
            byPassPatientFacing
          />
        </SearchView>
        <InnerContainer
          className={"list-container"}
          open={showSuggestions ? 1 : 0}
        >
          <SuggestionsList
            className={"suggestions-list"}
            showusersymptom={showUserSymptom ? "true" : "false"}
            items={filterSuggestions?.length || 0}
          >
            {filterSuggestions?.map(toSearchResultOption)}
          </SuggestionsList>
          {showUserSymptom &&
            props.userEnterSymptom === UserEnterSymptomsMode.FREE_TEXT && (
              <Fade timeout={300} in={true}>
                <AddSymptomContainer
                  ref={addSymptomRef}
                  className="add-symptom"
                >
                  <AddSymptomLabel>
                    {translate(
                      props?.enterSymptomTextOverride?.cantFindText ||
                        "CANT_FIND_A_SYMPTOM",
                    )}
                  </AddSymptomLabel>

                  <AddSymptomButton
                    className={"add-symptom-button"}
                    onClick={onEnterUserSymptom}
                  >
                    {`${translate("ADD_A_SYMPTOM")} '${term.trim()}'`}
                  </AddSymptomButton>
                </AddSymptomContainer>
              </Fade>
            )}
          {showUserSymptom &&
            props.userEnterSymptom === UserEnterSymptomsMode.TELL_US && (
              <AddSymptomContainer ref={addSymptomRef} className="add-symptom">
                <AddSymptomLabel>
                  {translate(
                    props?.enterSymptomTextOverride?.cantFindText ||
                      "CANT_FIND_A_SYMPTOM",
                  )}
                </AddSymptomLabel>
                <TellUsButton
                  className={"tell-us-button"}
                  onClick={onClickTellUsMore}
                >
                  {translate("TELL_US")}
                </TellUsButton>
              </AddSymptomContainer>
            )}
        </InnerContainer>
        <Fade timeout={300} in={showEnterSymptom} mountOnEnter unmountOnExit>
          <EnterUserSymptom
            userSymptom={term}
            onSymptomSent={onUserSymptomSent}
            {...props?.enterSymptomTextOverride}
          />
        </Fade>
      </SearchComponentView>
    );
  },
);
